import _ from 'lodash';
import { Reducer } from 'redux';
import ContentConstants from '../contentConstants';
import { IColumn, ICustomQuestion, IDimension, ILibraryQuestion, IQuestionVariation, SourceType } from '../models';
import * as customQuestionApi from '../services/CustomQuestionAPI';
import * as api from '../services/QuestionAPI';
import { LibraryQuestionColumnHelper } from '../utilities';
import * as Actions from './Actions';
import { ActionTypes } from './ActionTypes';

export interface ILibraryQuestionListState {
	questions: ILibraryQuestion[];
	loading: boolean;
	isDimensionsLoading: boolean;
	columns: IColumn[];
	dimensions: IDimension[];
}

export const defaultState: ILibraryQuestionListState = {
	questions: [],
	loading: false,
	isDimensionsLoading: false,
	columns: [],
	dimensions: [],
};

export const ActionCreators = {
	GetQuestions: (surveyId: number, defaultCultureCode: string, isDemographic: boolean) => (dispatch: any) => {
		dispatch({ type: ActionTypes.LIBRARY_QUESTIONS_GET_REQUESTED });

		return Promise.all([api.getQuestions(isDemographic), customQuestionApi.getCustomQuestions(surveyId, defaultCultureCode, isDemographic)])
			.then(([libraryQuestions, customQuestions]) => {
				const libraryQs = libraryQuestions.items.map((q) => ({ ...q, sourceType: SourceType.Library }));
				const questions = libraryQs.concat(
					customQuestions.map((cq: ICustomQuestion): ILibraryQuestion => {
						return {
							showVariations: false,
							questionVariations: [],
							languageCount: cq.languages.length,
							benchmarkIds: [],
							questionId: cq.questionId,
							text: cq.text,
							code: cq.code,
							isDemographic: cq.isDemographic,
							questionType: cq.questionType,
							languages: cq.languages,
							responseOptionGroup: cq.responseOptionGroup,
							sourceType: SourceType.Custom,
						};
					}),
				);

				dispatch({
					type: ActionTypes.LIBRARY_QUESTIONS_GET_SUCCEEDED,
					payload: {
						questions,
						customQuestions,
						columns: LibraryQuestionColumnHelper.getSurveyQuestionColumns(),
					},
				});
			})
			.catch((error: Error) => dispatch({ type: ActionTypes.LIBRARY_QUESTIONS_GET_FAILED, payload: error.message }));
	},
	GetDemographicQuestions: () => (dispatch: any) => {
		dispatch({ type: ActionTypes.LIBRARY_QUESTIONS_GET_REQUESTED });

		return api
			.getQuestions(true)
			.then((questions) =>
				dispatch({
					type: ActionTypes.LIBRARY_QUESTIONS_GET_SUCCEEDED,
					payload: {
						questions: questions.items.map((q) => ({ ...q, sourceType: SourceType.Library })),
						columns: LibraryQuestionColumnHelper.getDemographicQuestionColumns(),
					},
				}),
			)
			.catch((error: Error) => dispatch({ type: ActionTypes.LIBRARY_QUESTIONS_GET_FAILED, payload: error.message }));
	},
	ShowAllVariations: (showVariations: boolean) => (dispatch: any) => {
		dispatch({ type: ActionTypes.LIBRARY_QUESTION_SHOW_ALL_VARIATIONS, payload: showVariations });
	},
	ShowSingleVariation: (questionId: number) => (dispatch: any) => {
		dispatch({ type: ActionTypes.LIBRARY_QUESTION_SHOW_SINGLE_VARIATION, payload: questionId });
	},
	UpdateColumns: (columns?: IColumn[]) => (dispatch: any) => {
		dispatch({ type: ActionTypes.LIBRARY_QUESTION_UPDATE_COLUMNS, payload: columns });
	},
	SelectQuestion: (question: ILibraryQuestion, isSelected: boolean) => (dispatch: any) => {
		dispatch({ type: ActionTypes.LIBRARY_QUESTION_SELECTED, payload: { question, isSelected } });
		if (isSelected) {
			dispatch({ type: ActionTypes.TOAST_INFO_NOTIFICATION, payload: ContentConstants.TOAST_QUESTIONMODAL_ADDED });
			if (question.isDemographic) {
				dispatch({
					type: ActionTypes.CLIENT_PARTICIPANT_ATTRIBUTE_ADD_SELECTED_BYNAME,
					payload: question.attributeName,
				});
			}
		} else {
			dispatch({ type: ActionTypes.TOAST_INFO_NOTIFICATION, payload: ContentConstants.TOAST_QUESTIONMODAL_REMOVED });
			if (question.isDemographic) {
				dispatch({
					type: ActionTypes.CLIENT_PARTICIPANT_ATTRIBUTE_REMOVE_SELECTED_BYNAME,
					payload: question.attributeName,
				});
			}
		}
	},
	SelectQuestionVariation: (question: ILibraryQuestion, questionVariation: IQuestionVariation, isSelected: boolean) => (dispatch: any) => {
		dispatch({
			type: ActionTypes.LIBRARY_QUESTIONVARIATION_SELECTED,
			payload: { question, questionVariation, isSelected },
		});
		if (isSelected) {
			dispatch({ type: ActionTypes.TOAST_INFO_NOTIFICATION, payload: ContentConstants.TOAST_QUESTIONMODAL_ADDED });
			if (question.isDemographic) {
				dispatch({
					type: ActionTypes.CLIENT_PARTICIPANT_ATTRIBUTE_ADD_SELECTED_BYNAME,
					payload: question.attributeName,
				});
			}
		} else {
			dispatch({ type: ActionTypes.TOAST_INFO_NOTIFICATION, payload: ContentConstants.TOAST_QUESTIONMODAL_REMOVED });
			if (question.isDemographic) {
				dispatch({
					type: ActionTypes.CLIENT_PARTICIPANT_ATTRIBUTE_REMOVE_SELECTED_BYNAME,
					payload: question.attributeName,
				});
			}
		}
	},
	SelectDimension: (dimensionName: string) => {
		return { type: ActionTypes.LIBRARY_QUESTION_SELECT_DIMENSION, payload: { dimensionName } };
	},
};

type KnownActions =
	| Actions.ILibraryQuestionsGetRequested
	| Actions.ILibraryQuestionsGetSucceeded
	| Actions.ILibraryQuestionsGetFailed
	| Actions.ILibraryQuestionShowAllVariations
	| Actions.ILibraryQuestionShowSingleVariation
	| Actions.ILibraryQuestionUpdateColumns
	| Actions.ILibraryQuestionsSelectDimension;

export const reducer: Reducer<ILibraryQuestionListState, KnownActions> = (
	state: ILibraryQuestionListState | undefined,
	action: KnownActions,
): ILibraryQuestionListState => {
	if (!state) {
		state = { ...defaultState };
	}

	switch (action.type) {
		// QUESTIONS
		case ActionTypes.LIBRARY_QUESTIONS_GET_REQUESTED:
			return { ...state, loading: true };

		case ActionTypes.LIBRARY_QUESTIONS_GET_SUCCEEDED: {
			return {
				...state,
				questions: action.payload.questions,
				columns: action.payload.columns,
				loading: false,
			};
		}

		case ActionTypes.LIBRARY_QUESTIONS_GET_FAILED:
			return { ...state, loading: false };

		// VARIATIONS
		case ActionTypes.LIBRARY_QUESTION_SHOW_ALL_VARIATIONS: {
			const updatedQuestions = state.questions.map((question: ILibraryQuestion) => {
				if (question.questionVariations?.length) {
					return { ...question, showVariations: action.payload };
				}
				return question;
			});
			const updatedColumns = state.columns.map((c) => {
				if (c.name === 'alternateVersions') {
					return { ...c, checked: action.payload };
				}
				return c;
			});

			return { ...state, questions: updatedQuestions, columns: updatedColumns };
		}
		case ActionTypes.LIBRARY_QUESTION_SHOW_SINGLE_VARIATION: {
			const updatedQuestions = state.questions.map((question: ILibraryQuestion) => {
				if (action.payload !== question.questionId) {
					return question;
				}
				const questionClone = { ...question };
				questionClone.showVariations = !questionClone.showVariations;
				return questionClone;
			});
			return { ...state, questions: updatedQuestions };
		}

		// COLUMNS
		case ActionTypes.LIBRARY_QUESTION_UPDATE_COLUMNS:
			return { ...state, columns: action.payload };
	}
	return state;
};
